import { useUpdateEffect } from "@react-hookz/web";
import { differenceInMinutes } from "date-fns";
import { useAtomValue } from "jotai";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "~/components/ui/cn";
import { CalendarIcon } from "~/components/ui/icons/calendar";
import { ClockIcon } from "~/components/ui/icons/clock";
import { DocumentIcon } from "~/components/ui/icons/document";
import { DollarCircleIcon } from "~/components/ui/icons/dollar-circle";
import { EuroCircleIcon } from "~/components/ui/icons/euro-circle";
import { PackageIcon } from "~/components/ui/icons/package";
import { StatisticsIcon } from "~/components/ui/icons/statistics";
import { TagIcon } from "~/components/ui/icons/tag";
import {
  StatisticsContent,
  StatisticsGroup,
  StatisticsGroupStatistics,
  StatisticsTitle,
} from "~/components/ui/statistics";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import {
  StoreCurrency,
  StoreRegion,
  useStatsQuery,
} from "~/graphql/graphql.gen";
import useTitle from "~/hooks/use-title";
import { brandState } from "~/stores/brand";
import { activeSaleState } from "~/stores/sales";
import { formatNumber } from "~/utils/number";
import { Ratings } from "~/views/analytics/ratings";
import { Returns } from "~/views/analytics/returns";
import { SalesByDayAreaChart } from "~/views/analytics/sales-by-day-area-chart";
import { TopProducts } from "~/views/analytics/top-products";

export function Analytics() {
  const brand = useAtomValue(brandState);
  const { i18n, t } = useTranslation();

  useTitle(`${brand?.name ?? ""} ~ Analytics`);

  const [updatedAt, setUpdatedAt] = useState(new Date());

  const activeSale = useAtomValue(activeSaleState);

  const { data: statsData } = useStatsQuery(
    {
      saleId: activeSale?.id ?? "",
    },
    {
      enabled: !!activeSale?.id,
      refetchOnWindowFocus: false,
    },
  );

  useUpdateEffect(() => {
    if (statsData) {
      setUpdatedAt(new Date());
    }
  }, [statsData]);

  const getUpdated = () => {
    const time = differenceInMinutes(new Date(), updatedAt);
    if (time === 0) {
      return t("analytics.header_last_update");
    }
    if (time === 1) {
      return t("analytics.header_last_update_time", {
        x: time,
      });
    }
    return t("analytics.header_last_update_times", {
      x: time,
    });
  };

  const totalTurnover =
    brand?.store === StoreRegion.Fr
      ? (statsData?.statsV2.totalTurnoverTtc ?? 0) / 100
      : (statsData?.statsV2.totalTurnoverHt ?? 0) / 100;
  const totalBilling = statsData?.statsV2.isSubjectToVat
    ? (statsData.statsV2.totalBillingTtc || 0) / 100
    : (statsData?.statsV2.totalBillingHt ?? 0) / 100;

  return (
    <main className="relative flex-1 overflow-y-auto">
      <div className="m-4 max-w-[55rem] md:m-10">
        <header className="flex items-center justify-between">
          <h2 className="text-4xl font-bold leading-[3.75rem] text-gray-900">
            {t("sidebar.analytics")}
          </h2>
          <div className="flex space-x-6 text-gray-500">
            <p className="hidden items-center space-x-1 text-xs min-[961px]:flex">
              <CalendarIcon className="size-4 text-gray-500" />
              <span>{t("analytics.header_time")}</span>
            </p>
            <p className="flex items-center space-x-1 text-xs">
              <ClockIcon className="size-4 text-gray-500" />
              <span>{getUpdated()}</span>
            </p>
          </div>
        </header>
        <section className="py-6">
          <header className="mb-4 flex flex-col space-x-0 space-y-4 lg:flex-row lg:space-x-4 lg:space-y-0">
            <StatisticsGroup className="w-full" variant="secondary">
              <StatisticsGroupStatistics>
                <StatisticsTitle>
                  {activeSale?.currency === StoreCurrency.Usd ? (
                    <DollarCircleIcon className="size-4 shrink-0" />
                  ) : (
                    <EuroCircleIcon className="size-4 shrink-0" />
                  )}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger className="border-b border-dashed border-gray-300 text-xs font-semibold uppercase leading-none tracking-wider text-gray-700 hover:border-solid">
                        {t("analytics.number_total_sale")}
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <div className="p-1">
                          {brand?.store === StoreRegion.Fr
                            ? t("analytics.tooltip_total_sale")
                            : t("analytics.tooltip_total_sale_ht")}
                        </div>
                        <TooltipArrow />
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </StatisticsTitle>
                <StatisticsContent
                  className={cn("gap-1", {
                    "flex-row-reverse justify-end": i18n.language === "en",
                  })}
                >
                  <span className="text-4xl font-bold text-gray-900">
                    {formatNumber(
                      totalTurnover,
                      i18n.language === "fr" ? "fr" : "en",
                      {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      },
                    )}
                  </span>

                  <span className="text-2xl font-bold text-gray-700">
                    {activeSale?.currency === StoreCurrency.Usd ? "$" : "€"}
                  </span>
                </StatisticsContent>
              </StatisticsGroupStatistics>
              <StatisticsGroupStatistics>
                <StatisticsTitle className="justify-between">
                  <div className="flex items-center gap-2.25">
                    <DocumentIcon className="size-4 shrink-0" />

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="border-b border-dashed border-gray-300 text-xs font-semibold uppercase leading-none tracking-wider text-gray-700 hover:border-solid">
                          {t("analytics.number_total_invoice")}
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <div
                            dangerouslySetInnerHTML={{
                              __html: t("analytics.tooltip_total_invoice"),
                            }}
                            className="p-1"
                          ></div>
                          <TooltipArrow />
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <span className="text-xs capitalize text-beige-600">
                    {statsData?.statsV2.totalBillingHt
                      ? t("analytics.estimation")
                      : undefined}
                  </span>
                </StatisticsTitle>
                <StatisticsContent
                  className={cn("gap-1", {
                    "flex-row-reverse justify-end": i18n.language === "en",
                  })}
                >
                  <span className="text-4xl font-bold text-gray-900">
                    {formatNumber(
                      totalBilling,
                      i18n.language === "fr" ? "fr" : "en",
                      {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      },
                    )}
                  </span>

                  <span className="text-2xl font-bold text-gray-700">
                    {activeSale?.currency === StoreCurrency.Usd ? "$" : "€"}
                  </span>
                </StatisticsContent>
              </StatisticsGroupStatistics>
            </StatisticsGroup>
            <StatisticsGroup className="w-full">
              <StatisticsGroupStatistics>
                <StatisticsTitle>
                  <PackageIcon className="size-4 shrink-0" />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger className="border-b border-dashed border-gray-300 text-xs font-semibold uppercase leading-none tracking-wider text-gray-700 hover:border-solid">
                        {t("analytics.number_total_orders")}
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <div className="p-1">
                          {t("analytics.tooltip_total_orders")}
                        </div>
                        <TooltipArrow />
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </StatisticsTitle>
                <StatisticsContent>
                  <span className="text-4xl font-bold text-gray-900">
                    {statsData?.statsV2.orderCount ?? 0}
                  </span>
                </StatisticsContent>
              </StatisticsGroupStatistics>
              <StatisticsGroupStatistics>
                <StatisticsTitle>
                  <TagIcon className="size-4 shrink-0" />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger className="border-b border-dashed border-gray-300 text-xs font-semibold uppercase leading-none tracking-wider text-gray-700 hover:border-solid">
                        {t("analytics.number_total_products")}
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <div className="p-1">
                          {t("analytics.tooltip_total_products")}
                        </div>
                        <TooltipArrow />
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </StatisticsTitle>
                <StatisticsContent>
                  <span className="text-4xl font-bold text-gray-900">
                    {statsData?.statsV2.productSoldCount ?? 0}
                  </span>
                </StatisticsContent>
              </StatisticsGroupStatistics>
            </StatisticsGroup>
          </header>
          <div className="rounded border border-gray-300 p-6 pb-0">
            <div className="flex flex-col gap-4">
              <div className="flex items-center">
                <StatisticsIcon className="size-4" />

                <span className="ml-2.25 cursor-pointer border-b border-dashed border-gray-300 text-xs font-semibold uppercase tracking-wider text-gray-700 hover:border-solid">
                  {t("analytics.graph_day")}
                </span>
              </div>

              <SalesByDayAreaChart key={activeSale?.id} />
            </div>
          </div>

          <TopProducts key={`top-products-${activeSale?.id}`} />
          <h3 className="mb-8 mt-[56px] text-lg font-bold text-gray-900">
            {t("analytics.returns")}
          </h3>
          <Returns key={`returns-${activeSale?.id}`} />

          <div className="mb-8 mt-[56px] flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900">
              {t("analytics.rate")}
            </h3>
          </div>

          <Ratings
            key={`ratings-${activeSale?.id}`}
            stats={statsData?.statsV2}
          />
        </section>
      </div>
    </main>
  );
}
